import React, { useMemo, useState } from 'react';
import { ArrowLeft, BriefcaseBusiness, Loader2, LogIn, UserPlus } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Seo from '../components/Seo';

type AuthMode = 'login' | 'signup';

function sanitizePhone(value: string) {
  return value.replace(/\D/g, '').slice(0, 10);
}

async function ensureCandidateProfileForUser(user: { id: string; email?: string | null; user_metadata?: Record<string, any> | null }) {
  const phoneFromMeta = String(user.user_metadata?.phone || user.email?.replace(/@karya\.com$/i, '') || '').replace(/\D/g, '').slice(0, 10);
  const nameFromMeta = String(user.user_metadata?.name || '').trim();

  const { data: existingProfile, error: existingProfileError } = await supabase
    .from('candidates')
    .select('id')
    .eq('id', user.id)
    .maybeSingle();

  if (existingProfileError) throw existingProfileError;
  if (existingProfile) return;

  const { error: upsertError } = await supabase
    .from('candidates')
    .upsert({
      id: user.id,
      phone: phoneFromMeta || null,
      name: nameFromMeta || 'Jobisa Worker',
      skills: [],
      professions: [],
      experience_years: 0,
    }, {
      onConflict: 'id',
      ignoreDuplicates: false,
    });

  if (upsertError) throw upsertError;
}

export default function WorkerAuthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedNext = params.get('next');
  const nextPath = requestedNext && requestedNext.startsWith('/worker')
    ? requestedNext
    : '/worker/portal';

  const [mode, setMode] = useState<AuthMode>('login');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const email = `${phone}@karya.com`;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phone.length < 10 || password.length < 6) {
      setError('Enter your 10-digit phone number and password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: isLimited } = await supabase.rpc('is_rate_limited', {
        p_action: 'jobisa_web_worker_login',
        p_identifier: phone,
      });

      if (isLimited) {
        throw new Error('Too many login attempts. Please try again later.');
      }

      await supabase.rpc('log_rate_limit_attempt', {
        p_action: 'jobisa_web_worker_login',
        p_identifier: phone,
      });

      const { data: phoneExists, error: phoneCheckError } = await supabase
        .rpc('check_phone_exists', { phone_number: phone });

      if (phoneCheckError) throw phoneCheckError;
      if (!phoneExists) {
        throw new Error('No worker account found for this number. Create one below or continue on WhatsApp/app first.');
      }

      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;
      if (signInData.user) {
        await ensureCandidateProfileForUser(signInData.user as any);
      }

      navigate(nextPath, { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Could not log you in.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phone.length < 10 || password.length < 6 || !name.trim()) {
      setError('Enter your name, 10-digit phone number, and a password of at least 6 characters.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: existingCandidate, error: existingCandidateError } = await supabase
        .rpc('check_phone_exists', { phone_number: phone });

      if (existingCandidateError) throw existingCandidateError;
      if (existingCandidate) {
        throw new Error('A worker account already exists for this phone number. Please log in instead.');
      }

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            phone,
            role: 'candidate',
            name: name.trim(),
          },
        },
      });

      if (signUpError) throw signUpError;
      if (!data.user) throw new Error('Could not create your worker account.');

      const { error: profileError } = await supabase
        .from('candidates')
        .upsert({
          id: data.user.id,
          phone,
          name: name.trim(),
          city: city.trim() || null,
          skills: [],
          professions: [],
          experience_years: 0,
        }, {
          onConflict: 'id',
          ignoreDuplicates: false,
        });

      if (profileError) throw profileError;

      navigate(nextPath, { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Could not create your worker account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fffaf5] text-slate-900">
      <Seo
        title="Worker Login | Jobisa"
        description="Log in to Jobisa on the web, browse current roles, and apply directly from your worker account."
        path="/worker/login"
        noindex={true}
      />

      <div className="mx-auto flex min-h-screen max-w-6xl items-center px-6 py-12 lg:px-10">
        <div className="grid w-full gap-10 lg:grid-cols-[0.92fr_1.08fr]">
          <div className="rounded-[2rem] border border-orange-100 bg-white p-8 shadow-[0_20px_60px_-35px_rgba(249,115,22,0.22)] lg:p-10">
            <div className="mb-8 flex items-center justify-between">
              <Link to="/worker" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-900">
                <ArrowLeft size={16} />
                Back to worker site
              </Link>
              <span className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-orange-700">
                Worker Web
              </span>
            </div>

            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">
              Browse jobs on web
            </p>
            <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950">
              Log in or create a worker account
            </h1>
            <p className="mt-4 max-w-xl text-base leading-8 text-slate-600">
              Use the same worker credentials as the Jobisa app. Once you sign in, you can browse jobs, apply directly, and track your applications from the website too.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); }}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition ${mode === 'login' ? 'bg-slate-950 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}
              >
                Worker Login
              </button>
              <button
                type="button"
                onClick={() => { setMode('signup'); setError(''); }}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition ${mode === 'signup' ? 'bg-[#F97316] text-white' : 'border border-orange-200 bg-orange-50 text-orange-700 hover:border-orange-300 hover:bg-orange-100'}`}
              >
                Create account
              </button>
            </div>

            <div className="mt-10 rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-orange-600 shadow-sm">
                  <BriefcaseBusiness size={20} />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">What you can do on web</p>
                  <p className="text-sm text-slate-500">Browse roles, apply, and revisit your application history.</p>
                </div>
              </div>
              <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-600">
                <li>Use the same worker identity as the app, linked to your phone number.</li>
                <li>Apply to jobs directly from rich job cards on the website.</li>
                <li>Keep WhatsApp, app, and web compatible through one candidate profile.</li>
              </ul>
            </div>
          </div>

          <div className="rounded-[2rem] border border-orange-100 bg-white p-8 shadow-[0_20px_60px_-35px_rgba(15,23,42,0.15)] lg:p-10">
            {error ? (
              <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            {mode === 'login' ? (
              <form className="space-y-5" onSubmit={handleLogin}>
                <div>
                  <label className="mb-2 block text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Phone Number
                  </label>
                  <div className="flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <span className="mr-3 text-sm font-semibold text-slate-500">+91</span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                      placeholder="9876543210"
                      className="w-full bg-transparent text-lg text-slate-900 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-lg text-slate-900 outline-none focus:border-orange-300"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-4 text-base font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
                  Continue to Worker Jobs
                </button>
              </form>
            ) : (
              <form className="space-y-5" onSubmit={handleSignup}>
                <div>
                  <label className="mb-2 block text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Full Name
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-lg text-slate-900 outline-none focus:border-orange-300"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Phone Number
                  </label>
                  <div className="flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <span className="mr-3 text-sm font-semibold text-slate-500">+91</span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                      placeholder="9876543210"
                      className="w-full bg-transparent text-lg text-slate-900 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
                    City
                  </label>
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Vadodara"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-lg text-slate-900 outline-none focus:border-orange-300"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Choose a password"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-lg text-slate-900 outline-none focus:border-orange-300"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#F97316] px-5 py-4 text-base font-semibold text-white transition hover:bg-[#EA580C] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <UserPlus size={18} />}
                  Create Worker Account
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
